import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import { MdClose } from 'react-icons/md';
import successIconImg from '../assets/images/Success_Icon.png';
import deleteIconImg from '../assets/images/delete_icon.png';
import logoutIconImg from '../assets/images/logout_modle.png';
import forceChangeIconImg from '../assets/images/ForceChange.png';
import SuccessIcon from '../assets/images/SuccessIcon.png';
import DeactivateIcon from '../assets/images/DeactivateIcon.png';
import limitReachedIconImg from '../assets/images/LimitReached.png';
import UpgradeIcon from '../assets/images/UpgradeIcon.png';

const CommonModal = ({
  show,
  title,
  subtitle,
  message,
  logo,
  isLoading,
  redirectText,
  onConfirm,
  confirmText,
  type,
  children,
  style,
  disableFocusTrap = false,
  onCancel,
  cancelText,
  onClose
}) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const getFocusableElements = () => {
      if (!modalRef.current) return [];
      return Array.from(
        modalRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => {
        return (
          el.getAttribute('tabindex') !== '-1' &&
          !el.disabled &&
          (el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0)
        );
      });
    };

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab' || !modalRef.current || disableFocusTrap) return;

      // If focus is currently inside a different modal/dialog, ignore this event to let that modal handle it
      const activeDialog = document.activeElement ? document.activeElement.closest('[role="dialog"]') : null;
      if (activeDialog && activeDialog !== modalRef.current) {
        return;
      }

      const focusableElements = getFocusableElements();

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // If focus is not inside the modal, redirect it to the first element
      if (!modalRef.current.contains(document.activeElement)) {
        firstElement.focus();
        e.preventDefault();
        return;
      }

      if (e.shiftKey) {
        if (document.activeElement === firstElement || document.activeElement === modalRef.current) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };

    if (show && !disableFocusTrap) {
      document.addEventListener('keydown', handleKeyDown);
      // Accessibility: Focus the first interactive element or the modal itself
      setTimeout(() => {
        if (modalRef.current) {
          const focusableElements = getFocusableElements();
          if (focusableElements.length > 0) {
            focusableElements[0].focus();
          } else {
            modalRef.current.focus();
          }
        }
      }, 100);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [show, disableFocusTrap]);

  if (!show) return null;

  return (
    <div className="auth-modal-overlay" role="presentation">
      <div
        className={`auth-success-modal ${type === 'delete' ? 'delete-modal' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-message"
        tabIndex={-1}
        ref={modalRef}
        style={{ outline: 'none', ...style }}
      >
        {onClose && (
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
            disabled={isLoading}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              color: '#9E9E9E',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '6px',
              borderRadius: '50%',
              transition: 'all 0.2s ease',
              zIndex: 10
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = '#F5F5F5';
                e.currentTarget.style.color = '#333333';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#9E9E9E';
            }}
          >
            <MdClose />
          </button>
        )}
        {logo && <img src={logo} alt="Company Logo" className="modal-logo" aria-hidden="true" />}
        {type === 'success' && (
          <div className="modal-success-icon-container mb-3 text-center">
            <img
              src={successIconImg}
              alt="Success"
              className="modal-success-icon"
              style={{ width: '50px', height: '50px', objectFit: 'contain' }}
            />
          </div>
        )}
        {type === 'delete' && (
          <div className="modal-delete-icon-container mb-2 text-center">
            <img
              src={deleteIconImg}
              alt="Delete"
              className="modal-delete-icon"
              style={{ width: '50px', height: '50px', objectFit: 'contain' }}
            />
          </div>
        )}

        {type === 'logout' && (
          <div className="modal-logout-icon-container mb-3 text-center">
            <img
              src={logoutIconImg}
              alt="Logout"
              className="modal-logout-icon"
              style={{ width: '50px', height: '50px', objectFit: 'contain' }}
            />
          </div>
        )}

        {type === 'forcelogout' && (
          <div className="modal-success-icon-container mb-3 text-center">
            <img
              src={SuccessIcon}
              alt="Logout"
              className="modal-success-icon"
              style={{ width: '50px', height: '50px', objectFit: 'contain' }}
            />
          </div>
        )}

        {type === 'forceChange' && (
          <div className="modal-success-icon-container mb-3 text-center">
            <img
              src={forceChangeIconImg}
              alt="Force Change"
              className="modal-success-icon"
              style={{ width: '50px', height: '50px', objectFit: 'contain' }}
            />
          </div>
        )}

        {type === 'upgrade' && (
          <div className="modal-success-icon-container mb-3 text-center">
            <img
              src={UpgradeIcon}
              alt="Upgrade"
              className="modal-success-icon"
              style={{ width: '50px', height: '50px', objectFit: 'contain' }}
            />
          </div>
        )}

        {type === 'deactivate' && (
          <div className="modal-success-icon-container mb-3 text-center">
            <img
              src={DeactivateIcon}
              alt="Deactivate"
              className="modal-success-icon"
              style={{ width: '50px', height: '50px', objectFit: 'contain' }}
            />
          </div>
        )}

        {type === 'limitReached' && (
          <div className="modal-success-icon-container mb-3 text-center">
            <img
              src={limitReachedIconImg}
              alt="Limit Reached"
              className="modal-success-icon"
              style={{ width: '50px', height: '50px', objectFit: 'contain' }}
            />
          </div>
        )}

        <div className="text-center">
          {title && (
            <h3
              id="modal-title"
              className="mb-2"
              style={{ fontWeight: '700', color: '#212121', fontSize: '24px', overflowWrap: 'break-word' }}
            >
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="mb-2" style={{ fontSize: '14px', lineHeight: '1.5', color: '#333333' }}>
              {subtitle}
            </p>
          )}
          {message && (
            <p
              id="modal-message"
              aria-live="assertive"
              className="mb-0 px-2"
              style={{
                fontSize: '18px',
                color: '#333333',
                fontWeight: '400',
                overflowWrap: 'break-word',
                maxHeight: '250px',
                overflowY: 'auto'
              }}
            >
              {message}
            </p>
          )}
        </div>

        {children && <div className="modal-body-custom">{children}</div>}

        {isLoading && (
          <div className="loader-spinner mt-3 mb-2 text-center" role="status" aria-label="Processing">
            <Spinner animation="border" variant={type === 'success' ? 'success' : 'dark'} size="md" aria-hidden="true" />
          </div>
        )}
        {redirectText && (
          <div
            className="redirect-text text-center mt-2 mb-2"
            aria-live="polite"
            style={{ color: '#333', fontSize: '15px', fontWeight: '500' }}
          >
            {redirectText}
          </div>
        )}
        {(onCancel || onConfirm) && (
          <div className="d-flex justify-content-center mt-4" style={{ gap: '12px' }}>
            {onCancel && (
              <button
                className="btn-modal-cancel"
                onClick={onCancel}
                disabled={isLoading}
                style={{
                  backgroundColor: '#fff',
                  color: '#333',
                  border: '1px solid #6D6E71',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  borderRadius: '6px',
                  padding: '8px 24px',
                  flex: 1,
                  fontSize: '14px'
                }}
              >
                {cancelText || 'Cancel'}
              </button>
            )}
            {onConfirm && (
              <button
                className="btn-modal-confirm mt-3"
                onClick={onConfirm}
                disabled={isLoading}
                style={{
                  backgroundColor: type === 'delete' ? '#A52A2A' : isLoading ? '#E0E0E0' : '#005457',
                  color: isLoading ? '#9E9E9E' : '#fff',
                  border: 'none',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  fontWeight: '600',
                  borderRadius: '6px',
                  padding: '8px 24px',
                  flex: 1,
                  fontSize: '14px'
                }}
              >
                {confirmText || 'Confirm'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

CommonModal.propTypes = {
  show: PropTypes.bool.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  message: PropTypes.string,
  logo: PropTypes.string,
  isLoading: PropTypes.bool,
  redirectText: PropTypes.string,
  onConfirm: PropTypes.func,
  confirmText: PropTypes.string,
  type: PropTypes.string,
  children: PropTypes.node,
  style: PropTypes.object,
  disableFocusTrap: PropTypes.bool,
  onCancel: PropTypes.func,
  cancelText: PropTypes.string,
  onClose: PropTypes.func
};

export default CommonModal;
